/**
 * Pitch grades on the score: a coloured ring around each graded notehead of
 * my part, plus a small label where there is something to say (how many
 * cents sharp or flat, or which note was sung instead). Drawn into OSMD's
 * own SVG like the step hints, and redrawn whenever OSMD wipes the sheet.
 */
import { pitchName } from '../../utils/noteSpelling';

const GROUP_CLASS = 'pitch-grades';
const SVG_NS = 'http://www.w3.org/2000/svg';
const MINUS = '−';

// Reaching a note by sliding into it, marked beside the grade
export const SCOOP_COLOR = '#7c3aed';
export const SCOOP_UP = '↗';
export const SCOOP_DOWN = '↘';

export const GRADE_STYLES = {
  good: { color: '#16a34a', label: 'in tune (±25¢)' },
  close: { color: '#d97706', label: 'within 50¢' },
  off: { color: '#dc2626', label: 'another note' },
  missed: { color: '#94a3b8', label: 'not heard', dashed: true }
};

const measureRows = (osmd) => osmd?.GraphicSheet?.MeasureList || osmd?.graphic?.measureList || [];

function scoreContainer(osmd) {
  return osmd?.container || osmd?.Drawer?.Backends?.[0]?.getSvgElement?.()?.parentNode || null;
}

// Source note -> graphical note, for the notes currently drawn
function graphicalNotes(osmd) {
  const map = new Map();
  for (const row of measureRows(osmd)) {
    for (const measure of row || []) {
      for (const entry of measure?.staffEntries || []) {
        for (const voiceEntry of entry.graphicalVoiceEntries || []) {
          for (const graphicalNote of voiceEntry.notes || []) {
            if (graphicalNote.sourceNote) map.set(graphicalNote.sourceNote, graphicalNote);
          }
        }
      }
    }
  }
  return map;
}

function svgRect(svg, element) {
  const r = element.getBoundingClientRect();
  if (!r.width && !r.height) return null;
  const ctm = svg.getScreenCTM();
  if (!ctm) return null;
  const inverse = ctm.inverse();
  const toSvg = (x, y) => {
    const point = svg.createSVGPoint();
    point.x = x;
    point.y = y;
    return point.matrixTransform(inverse);
  };
  const tl = toSvg(r.left, r.top);
  const br = toSvg(r.right, r.bottom);
  return { left: tl.x, top: tl.y, right: br.x, bottom: br.y, width: br.x - tl.x, height: br.y - tl.y, cx: (tl.x + br.x) / 2, cy: (tl.y + br.y) / 2 };
}

// The notehead of this note: in a chord, the one at the note's pitch rank
function noteheadOf(graphicalNote) {
  const group = graphicalNote.getSVGGElement?.();
  const svg = group?.ownerSVGElement;
  if (!svg) return null;
  const heads = [];
  group.querySelectorAll('.vf-notehead').forEach((element) => {
    const rect = svgRect(svg, element);
    if (rect) heads.push(rect);
  });
  if (heads.length === 0) {
    const whole = svgRect(svg, group);
    return whole ? { svg, head: whole } : null;
  }
  if (heads.length === 1) return { svg, head: heads[0] };
  heads.sort((a, b) => a.top - b.top);
  const siblings = (graphicalNote.parentVoiceEntry?.notes || [])
    .filter(n => n.sourceNote?.Pitch)
    .sort((a, b) => b.sourceNote.Pitch.getHalfTone() - a.sourceNote.Pitch.getHalfTone());
  const rank = siblings.indexOf(graphicalNote);
  return { svg, head: heads[rank >= 0 && siblings.length === heads.length ? rank : 0] };
}

const labelFor = (result) => {
  if (result.grade === 'close') return result.cents > 0 ? `+${result.cents}` : `${MINUS}${-result.cents}`;
  if (result.grade === 'off') return pitchName(result.sung, result.fifths);
  return null;
};

// The drawn mark plus its ring, which hover testing measures
function mark(head, result) {
  const style = GRADE_STYLES[result.grade];
  const g = document.createElementNS(SVG_NS, 'g');
  const size = Math.max(6, head.height);
  const ring = document.createElementNS(SVG_NS, 'ellipse');
  ring.setAttribute('cx', String(head.cx));
  ring.setAttribute('cy', String(head.cy));
  ring.setAttribute('rx', String(head.width / 2 + size * 0.45));
  ring.setAttribute('ry', String(head.height / 2 + size * 0.4));
  ring.setAttribute('fill', style.color);
  ring.setAttribute('fill-opacity', result.grade === 'missed' ? '0.08' : '0.16');
  ring.setAttribute('stroke', style.color);
  ring.setAttribute('stroke-width', String(Math.max(1, size * 0.16)));
  if (style.dashed) ring.setAttribute('stroke-dasharray', `${size * 0.35} ${size * 0.25}`);
  g.appendChild(ring);

  const text = labelFor(result);
  if (text) {
    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('x', String(head.cx));
    label.setAttribute('y', String(head.top - size * 0.75));
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('font-family', 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif');
    label.setAttribute('font-weight', '700');
    label.setAttribute('font-size', String(size * 1.05));
    label.setAttribute('fill', style.color);
    // White halo keeps the label readable over staff lines and stems
    label.setAttribute('stroke', '#ffffff');
    label.setAttribute('stroke-width', String(size * 0.3));
    label.setAttribute('stroke-linejoin', 'round');
    label.setAttribute('paint-order', 'stroke');
    label.textContent = text;
    g.appendChild(label);
  }

  // A scoop (or a slide down into the note) gets its own arrow, left of
  // the ring: it is about how the note was reached, not its pitch
  if (result.scoop) {
    const arrow = document.createElementNS(SVG_NS, 'text');
    arrow.setAttribute('x', String(head.left - size * 0.75));
    arrow.setAttribute('y', String(head.cy));
    arrow.setAttribute('text-anchor', 'end');
    arrow.setAttribute('dominant-baseline', 'central');
    arrow.setAttribute('font-family', 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif');
    arrow.setAttribute('font-weight', '700');
    arrow.setAttribute('font-size', String(size * 1.15));
    arrow.setAttribute('fill', SCOOP_COLOR);
    arrow.setAttribute('stroke', '#ffffff');
    arrow.setAttribute('stroke-width', String(size * 0.3));
    arrow.setAttribute('stroke-linejoin', 'round');
    arrow.setAttribute('paint-order', 'stroke');
    arrow.textContent = result.scoop.direction === 'up' ? SCOOP_UP : SCOOP_DOWN;
    g.appendChild(arrow);
  }
  return { g, ring };
}

export function clearGradeOverlay(osmd) {
  const container = scoreContainer(osmd);
  if (!container) return;
  container.querySelectorAll(`g.${GROUP_CLASS}`).forEach((group) => group.remove());
}

/** Draws the grades; returns the marks drawn, [{ ring, result }]. */
export function drawGradeOverlay(osmd, results) {
  clearGradeOverlay(osmd);
  if (!osmd) return [];
  const notes = graphicalNotes(osmd);
  const groups = new Map(); // svg element -> our <g>
  const marks = [];
  for (const result of results) {
    const graphicalNote = notes.get(result.ref);
    if (!graphicalNote) continue; // part hidden, or the sheet was reloaded
    const placement = noteheadOf(graphicalNote);
    if (!placement) continue;
    if (!groups.has(placement.svg)) {
      const group = document.createElementNS(SVG_NS, 'g');
      group.setAttribute('class', GROUP_CLASS);
      // Clicks go through to the score (bar selection); hovering is
      // detected from the pointer position instead (watchGradeHover)
      group.setAttribute('pointer-events', 'none');
      groups.set(placement.svg, group);
    }
    const { g, ring } = mark(placement.head, result);
    groups.get(placement.svg).appendChild(g);
    marks.push({ ring, result });
  }
  // Always leave a (possibly empty) group behind so the observer can tell
  // "drawn" from "wiped by a re-render"
  const svgs = groups.size ? [...groups.keys()] : [...(scoreContainer(osmd)?.querySelectorAll('svg') || [])];
  svgs.forEach((svg) => {
    const group = groups.get(svg) || (() => {
      const g = document.createElementNS(SVG_NS, 'g');
      g.setAttribute('class', GROUP_CLASS);
      return g;
    })();
    svg.appendChild(group);
  });
  return marks;
}

/**
 * Draw now and keep the grades on the score across OSMD re-renders;
 * `onMarks` hears about the marks after every draw. Returns a stop
 * function that also removes them.
 */
export function watchGradeOverlay(osmd, results, onMarks = () => {}) {
  const container = scoreContainer(osmd);
  let frame = 0;
  let stopped = false;
  const redraw = () => {
    frame = 0;
    if (!stopped) onMarks(drawGradeOverlay(osmd, results));
  };
  onMarks(drawGradeOverlay(osmd, results));
  let observer = null;
  if (container && typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(() => {
      if (!frame && !container.querySelector(`g.${GROUP_CLASS}`)) frame = requestAnimationFrame(redraw);
    });
    observer.observe(container, { childList: true, subtree: true });
  }
  return () => {
    stopped = true;
    if (observer) observer.disconnect();
    if (frame) cancelAnimationFrame(frame);
    clearGradeOverlay(osmd);
    onMarks([]);
  };
}

const HOVER_CLASS = 'pitch-grade-hover';
const HOVER_SLACK_PX = 4;

/**
 * Report which graded note the pointer is over: onHover({ result, rect })
 * with the ring's screen rectangle, or onHover(null). Measured from the
 * pointer position, since the marks themselves let clicks through.
 * Returns a stop function.
 */
export function watchGradeHover(osmd, getMarks, onHover) {
  const container = scoreContainer(osmd);
  if (!container) return () => {};
  let frame = 0;
  let pointer = null;
  let current = null;
  const report = (hit) => {
    if (hit?.result === current?.result) return;
    current = hit;
    container.classList.toggle(HOVER_CLASS, Boolean(hit));
    onHover(hit);
  };
  const test = () => {
    frame = 0;
    if (!pointer) return;
    let hit = null;
    let nearest = Infinity;
    for (const { ring, result } of getMarks()) {
      const r = ring.getBoundingClientRect();
      if (pointer.x < r.left - HOVER_SLACK_PX || pointer.x > r.right + HOVER_SLACK_PX
        || pointer.y < r.top - HOVER_SLACK_PX || pointer.y > r.bottom + HOVER_SLACK_PX) continue;
      const distance = Math.hypot(pointer.x - (r.left + r.right) / 2, pointer.y - (r.top + r.bottom) / 2);
      if (distance < nearest) {
        nearest = distance;
        hit = { result, rect: { left: r.left, top: r.top, right: r.right, bottom: r.bottom } };
      }
    }
    report(hit);
  };
  const onMove = (event) => {
    pointer = { x: event.clientX, y: event.clientY };
    if (!frame) frame = requestAnimationFrame(test);
  };
  const onLeave = () => {
    pointer = null;
    report(null);
  };
  // Scrolling moves the score under a still pointer
  const onScroll = () => {
    if (pointer && !frame) frame = requestAnimationFrame(test);
  };
  container.addEventListener('mousemove', onMove, { passive: true });
  container.addEventListener('mouseleave', onLeave);
  // Scroll events do not bubble; capture them page-wide (the score may
  // scroll inside an element around the container)
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  return () => {
    container.removeEventListener('mousemove', onMove);
    container.removeEventListener('mouseleave', onLeave);
    window.removeEventListener('scroll', onScroll, { capture: true });
    if (frame) cancelAnimationFrame(frame);
    container.classList.remove(HOVER_CLASS);
    if (current) onHover(null);
  };
}

// The highlighted bars, as the measure highlighter draws them (one
// rectangle per system the selection spans)
const SELECTION_SELECTOR = 'rect[data-range-highlight]';
// How far the rectangle may move (scrolling) before the hover is reported again
const RANGE_MOVE_PX = 4;

/**
 * Calls onHover({ rect }) while the pointer is over the highlighted bars
 * (rect: the client rectangle of the system it is over) and onHover(null)
 * when it leaves them. Returns a function that stops watching.
 */
export function watchRangeHover(osmd, onHover) {
  const container = scoreContainer(osmd);
  if (!container) return () => {};
  let frame = 0;
  let pointer = null;
  let current = null;
  const report = (rect) => {
    const same = rect && current
      && Math.abs(rect.left - current.left) < RANGE_MOVE_PX && Math.abs(rect.top - current.top) < RANGE_MOVE_PX;
    if (same || (!rect && !current)) return;
    current = rect;
    onHover(rect ? { rect } : null);
  };
  const test = () => {
    frame = 0;
    if (!pointer) return;
    let hit = null;
    container.querySelectorAll(SELECTION_SELECTOR).forEach((element) => {
      const r = element.getBoundingClientRect();
      if (pointer.x >= r.left && pointer.x <= r.right && pointer.y >= r.top && pointer.y <= r.bottom) {
        hit = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
      }
    });
    report(hit);
  };
  const onMove = (event) => {
    pointer = { x: event.clientX, y: event.clientY };
    if (!frame) frame = requestAnimationFrame(test);
  };
  const onLeave = () => {
    pointer = null;
    report(null);
  };
  const onScroll = () => {
    if (pointer && !frame) frame = requestAnimationFrame(test);
  };
  container.addEventListener('mousemove', onMove, { passive: true });
  container.addEventListener('mouseleave', onLeave);
  window.addEventListener('scroll', onScroll, { passive: true, capture: true });
  return () => {
    container.removeEventListener('mousemove', onMove);
    container.removeEventListener('mouseleave', onLeave);
    window.removeEventListener('scroll', onScroll, { capture: true });
    if (frame) cancelAnimationFrame(frame);
    if (current) onHover(null);
  };
}
